import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { PencilLine } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { HsId, PageHeader, Row, Section, Spinner, StatusBadge } from "@/components/kit";
import ApprovalBar from "@/components/ApprovalBar";
import ActivityList from "@/components/ActivityList";
import Documents from "@/components/Documents";
import { ago, fdate, firstName, inr, num, pct } from "@/lib/format";

const DIFF_KEYS = ["date", "quantity", "price", "charges", "total_value", "amount", "executed_by_person_id", "demat_account_id",
  "new_stake_pct", "notes", "from_person_id", "to_person_id", "payment_mode", "reference", "holding_id"];

export function Revisions({ revisions, nameOf = (x) => x }) {
  if (!revisions?.length) return null;
  return (
    <Section title="Edit history">
      <div className="space-y-2.5" data-testid="revisions-list">
        {revisions.map((r) => {
          const prev = r.previous?.transaction || r.previous || {};
          const nw = r.new?.transaction || r.new || {};
          const changes = DIFF_KEYS.filter((k) => k in prev && String(prev[k] ?? "") !== String(nw[k] ?? ""));
          const pa = (r.previous?.allocations || []).map((a) => `${nameOf(a.person_id)} ${pct(a.percentage)}`).join(", ");
          const na = (r.new?.allocations || []).map((a) => `${nameOf(a.person_id)} ${pct(a.percentage)}`).join(", ");
          const pf = (r.previous?.funding || []).map((a) => `${nameOf(a.person_id)} ${inr(a.amount)}`).join(", ");
          const nf = (r.new?.funding || []).map((a) => `${nameOf(a.person_id)} ${inr(a.amount)}`).join(", ");
          return (
            <div key={r.id} className="hd-card" data-testid={`revision-${r.version}`}>
              <div className="text-sm font-semibold">Version {r.version} → {r.version + 1} · {r.edited_by_name}</div>
              <div className="text-xs text-[#718096]">{ago(r.created_at)}{r.was_confirmed ? " · was confirmed" : ""}{r.comment ? ` · “${r.comment}”` : ""}</div>
              <div className="mt-2 space-y-1 text-xs">
                {changes.map((k) => (
                  <div key={k}><span className="font-semibold">{k.replace(/_/g, " ")}:</span> <span className="line-through text-red-700/80">{k.endsWith("person_id") ? nameOf(prev[k]) : String(prev[k] ?? "—")}</span> → <span className="text-emerald-700">{k.endsWith("person_id") ? nameOf(nw[k]) : String(nw[k] ?? "—")}</span></div>
                ))}
                {pa !== na && <div><span className="font-semibold">ownership:</span> <span className="line-through text-red-700/80">{pa}</span> → <span className="text-emerald-700">{na}</span></div>}
                {pf !== nf && <div><span className="font-semibold">funding:</span> <span className="line-through text-red-700/80">{pf || "—"}</span> → <span className="text-emerald-700">{nf || "—"}</span></div>}
                {!changes.length && pa === na && pf === nf && <div className="text-[#718096]">No value changes</div>}
              </div>
            </div>
          );
        })}
      </div>
    </Section>
  );
}

export function Approvals({ approvals }) {
  if (!approvals?.length) return null;
  return (
    <Section title="Confirmations & comments">
      <div className="hd-card py-1">
        {approvals.map((a) => (
          <div key={a.id} className="py-2.5 border-b border-[#E2D9CF]/50 last:border-0 text-sm">
            <span className="font-semibold">{a.by_name}</span> {a.action === "accept" ? "accepted" : a.action === "reject" ? "rejected" : "requested an edit"} (v{a.version})
            {a.comment && <div className="text-[#4A5568] mt-0.5">“{a.comment}”</div>}
            <div className="text-xs text-[#718096]">{ago(a.created_at)}</div>
          </div>
        ))}
      </div>
    </Section>
  );
}

function Banner({ t, me }) {
  const last = t.approvals?.[0];
  if (t.status === "edit_requested")
    return <div className="hd-card bg-blue-50 border-blue-200 text-sm text-blue-800" data-testid="edit-requested-banner"><b>{last?.by_name}</b> requested an edit: “{last?.comment}”. {t.submitted_by === me ? "Please edit and resubmit." : "Waiting for the submitter."}</div>;
  if (t.status === "rejected")
    return <div className="hd-card bg-red-50 border-red-200 text-sm text-red-800" data-testid="rejected-banner">Rejected by <b>{last?.by_name}</b>: “{last?.comment}”. Rejected entries never affect balances.</div>;
  if (t.status === "pending" && !t.can_act)
    return <div className="hd-card bg-amber-50 border-amber-200 text-sm text-amber-800" data-testid="awaiting-banner">Waiting for the other Karta to confirm.</div>;
  return null;
}

export default function TxnDetail() {
  const { id } = useParams();
  const { person } = useAuth();
  const [t, setT] = useState(null);
  const load = () => api.get(`/transactions/${id}`).then((r) => setT(r.data));
  useEffect(() => {
    load();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!t) return <Spinner />;
  const nameOf = (pid) => [...t.allocations, ...t.funding].find((x) => x.person_id === pid)?.full_name || (pid === t.executed_by_person_id ? t.executed_by_name : pid?.slice(0, 6));
  const receipt = ["sell", "partial_exit", "complete_exit"].includes(t.type);
  return (
    <div className="pb-6">
      <PageHeader title={`${t.type_label}`} subtitle={<Link to={`/holding/${t.holding_id}`} className="text-[#C8553D] font-semibold" data-testid="txn-holding-link">{t.asset_name}</Link>}
        right={<StatusBadge status={t.status} testid="txn-status" />} />
      <div className="space-y-4">
        {t.can_act && <ApprovalBar endpoint={`/transactions/${id}/action`} onDone={load} />}
        <Banner t={t} me={person.id} />
        <div className="hd-card">
          <div className="text-xs text-[#718096]">{receipt ? "Net proceeds" : "Total value"}</div>
          <div className="text-3xl font-bold tabular-nums" data-testid="txn-total">{inr(t.total_value)}</div>
          <div className="mt-3">
            <Row label="Date">{fdate(t.date)}</Row>
            {t.quantity && <Row label="Quantity">{num(t.quantity)} {t.unit}</Row>}
            {t.price && <Row label={t.category === "commodity" ? "Rate" : "Price"}>{inr(t.price)}</Row>}
            {t.charges && <Row label="Brokerage / charges">{inr(t.charges)}</Row>}
            {t.lot_number && <Row label="Acquisition lot">Lot #{t.lot_number}</Row>}
            {t.sold_lot_number && <Row label="Sold from">Lot #{t.sold_lot_number}</Row>}
            {t.new_stake_pct && <Row label="New shareholding">{pct(t.new_stake_pct)}</Row>}
            <Row label="Executed by">{t.executed_by_name}</Row>
            {t.demat && <Row label="Custody (Demat)">{t.demat.account_name} · {t.demat.broker} · {t.demat.holder_name}</Row>}
            <Row label="Created">{t.created_by_name} · {ago(t.created_at)}</Row>
            {t.confirmed_by_name && <Row label="Confirmed">{t.confirmed_by_name} · {ago(t.confirmed_at)}</Row>}
            <Row label="Version">v{t.version}</Row>
            {t.notes && <Row label="Notes">{t.notes}</Row>}
          </div>
          {t.can_edit && (
            <Link to={`/add/${t.category}?edit=${t.id}`} className="hd-btn-outline w-full mt-3 h-11 text-sm" data-testid="txn-edit-button">
              <PencilLine size={16} /> {t.status === "confirmed" ? "Correct this entry (needs re-confirmation)" : "Edit"}
            </Link>
          )}
        </div>
        {t.allocations.length > 0 && (
          <Section title="Ownership" className="mt-0">
            <div className="hd-card py-1" data-testid="txn-allocations">
              {t.allocations.map((a) => (
                <div key={a.person_id} className="flex items-center justify-between py-2.5 border-b border-[#E2D9CF]/50 last:border-0">
                  <div><div className="text-sm font-semibold">{a.full_name}</div><div className="flex gap-1.5 items-center mt-0.5"><HsId value={a.hs_id} /><span className="text-[11px] text-[#718096]">{firstName(a.side_name)} Side</span></div></div>
                  <div className="font-bold">{pct(a.percentage)}</div>
                </div>
              ))}
            </div>
          </Section>
        )}
        {t.funding.length > 0 && (
          <Section title={receipt ? "Proceeds received by" : "Payment / funding"} className="mt-0">
            <div className="hd-card py-1" data-testid="txn-funding">
              {t.funding.map((f) => (
                <div key={f.person_id} className="flex items-center justify-between py-2.5 border-b border-[#E2D9CF]/50 last:border-0">
                  <div><div className="text-sm font-semibold">{f.full_name}</div><div className="flex gap-1.5 items-center mt-0.5"><HsId value={f.hs_id} /></div></div>
                  <div className="font-bold tabular-nums">{inr(f.amount)}</div>
                </div>
              ))}
            </div>
          </Section>
        )}
        {t.side_breakdown?.length > 0 && (
          <div className="hd-card bg-[#FBF8F4]" data-testid="txn-side-breakdown">
            <div className="hd-label mb-2">{receipt ? "Entitled vs received" : "Expected vs actual contribution"}</div>
            {t.side_breakdown.map((s) => (
              <div key={s.karta_id} className="flex justify-between text-sm py-1">
                <span className="font-semibold">{firstName(s.name)} Side</span>
                <span className="tabular-nums">{inr(s.expected)} · {receipt ? "got" : "paid"} {inr(s.actual)} <b className={Number(s.net) > 0 ? "text-emerald-700" : Number(s.net) < 0 ? "text-[#B34832]" : ""}>{Number(s.net) > 0 ? `+${inr(s.net)}` : Number(s.net) < 0 ? `−${inr(-s.net)}` : ""}</b></span>
              </div>
            ))}
            <div className="text-xs text-[#718096] mt-1">+ means the side is owed money for this entry. Counts once confirmed.</div>
          </div>
        )}
        <Section title="Documents" className="mt-0"><Documents entityType="transaction" entityId={t.id} /></Section>
        <Approvals approvals={t.approvals} />
        <Revisions revisions={t.revisions} nameOf={nameOf} />
        <Section title="Activity"><ActivityList items={t.activity} testid="txn-activity" /></Section>
      </div>
    </div>
  );
}
